import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { Share2, Facebook, Twitter, Link2, MessageCircle, Check } from "lucide-react";

/**
 * ShareButtons component - Social sharing for homestays and blog posts
 * @param {string} url - The URL to share
 * @param {string} title - The title/name to share
 * @param {string} description - Optional description for sharing
 * @param {string} variant - "inline" for horizontal buttons, "dropdown" for popover
 */
const ShareButtons = ({ url, title, description = "", variant = "dropdown" }) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const shareUrl = url || window.location.href;
  const shareText = description || title;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(shareText);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], "_blank", "width=600,height=400");
    setIsOpen(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          toast.error("Failed to share");
        }
      }
    }
  };

  // Inline variant - horizontal buttons
  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2" data-testid="share-buttons-inline">
        <span className="text-sm text-muted-foreground mr-1">Share:</span>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 w-9 p-0 hover:bg-green-50 hover:text-green-600 hover:border-green-200"
          onClick={() => handleShare("whatsapp")}
          title="Share on WhatsApp"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 w-9 p-0 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
          onClick={() => handleShare("facebook")}
          title="Share on Facebook"
          data-testid="share-facebook"
        >
          <Facebook className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 w-9 p-0 hover:bg-sky-50 hover:text-sky-500 hover:border-sky-200"
          onClick={() => handleShare("twitter")}
          title="Share on Twitter"
          data-testid="share-twitter"
        >
          <Twitter className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full h-9 w-9 p-0"
          onClick={handleCopyLink}
          title="Copy link"
          data-testid="share-copy"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  // Dropdown variant - popover with share options
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          data-testid="share-button"
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="space-y-1" data-testid="share-dropdown">
          <button
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-green-50 hover:text-green-600 transition-colors"
            onClick={() => handleShare("whatsapp")}
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
          <button
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors"
            onClick={() => handleShare("facebook")}
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </button>
          <button
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-sky-50 hover:text-sky-500 transition-colors"
            onClick={() => handleShare("twitter")}
          >
            <Twitter className="h-4 w-4" />
            Twitter
          </button>
          <div className="border-t my-1" />
          <button
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
            onClick={handleCopyLink}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4" />
                Copy Link
              </>
            )}
          </button>
          {navigator.share && (
            <>
              <div className="border-t my-1" />
              <button
                className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                onClick={handleNativeShare}
              >
                <Share2 className="h-4 w-4" />
                More Options...
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareButtons;
